import React, { useEffect, useState } from "react";
import { Plus, Download, ArrowRight, X, Ban, Pencil, Trash2, Printer, PackageCheck, Tag, AlertTriangle, Scissors, Filter, ChevronDown } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Field, Input, Select, Textarea, Modal, LengthInput, ConfirmDialog } from "@/components/Primitives";
import { CutPlanModal } from "@/components/CutPlanModal";
import { ContainerEstimate, stockBundleMap, withStockBundles } from "@/components/ContainerEstimate";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtNumber, formatApiErrorDetail, API, can, COLOURS, LENGTHS, saveBlob, BUTT_COLOUR_MAP, colourForButt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const blankLine = () => ({
  product_id: "", product_name: "", article_number: "",
  butt_size: "", length: "", colour: "", required_quantity: "",
  requires_pointing: false, bundled: false, poles_per_bundle: "",
});

// Debounced duplicate-order-number warning shown under the Order Number input.
function OrderDupWarning({ value, excludeId }) {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    const on = (value || "").trim();
    if (!on) { setMsg(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/orders/check-duplicate", { params: { order_number: on, exclude_id: excludeId || undefined } });
        if (r.data.duplicate) {
          const o = r.data.order;
          const text = `Duplicate! Order # "${on}" already exists${o?.customer_name ? ` for ${o.customer_name}` : ""}${o?.status ? ` (${o.status.replace(/_/g, " ")})` : ""}`;
          setMsg(text);
          toast.warning(text);
        } else setMsg(null);
      } catch { setMsg(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [value, excludeId]);
  if (!msg) return null;
  return (
    <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1" data-testid="order-dup-warning">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {msg}
    </div>
  );
}

const empty = {
  order_number: "",
  customer_id: "", customer_name: "",
  export_destination: "",
  special_instructions: "",
  order_date: new Date().toISOString().slice(0, 10),
  line_items: [blankLine()],
};

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

const STATUS_OPTS = [
  ["open", "Open"],
  ["partially_allocated", "Partially Allocated"],
  ["fully_allocated", "Fully Allocated"],
  ["partially_dispatched", "Partially Dispatched"],
  ["dispatched", "Dispatched"],
  ["cancelled", "Cancelled"],
];

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [colourMap, setColourMap] = useState(BUTT_COLOUR_MAP);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [statuses, setStatuses] = useState([]);          // multi-select status filter
  const [statusMode, setStatusMode] = useState("show");  // show | hide selected
  const [statusOpen, setStatusOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [delOrder, setDelOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [bundleForm, setBundleForm] = useState(null);
  const [matching, setMatching] = useState([]);
  const [showCutPlan, setShowCutPlan] = useState(false);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [matchFilter, setMatchFilter] = useState({ butt: "", length: "", colour: "", q: "" });
  const [autoQty, setAutoQty] = useState("");
  const [labelModal, setLabelModal] = useState(false);
  const [labelIds, setLabelIds] = useState([]);
  const [labelSpecial, setLabelSpecial] = useState("");
  const [labelPaste, setLabelPaste] = useState("");
  const [labelPasteResult, setLabelPasteResult] = useState(null);
  const [labelFields, setLabelFields] = useState([]);
  const [printingLabels, setPrintingLabels] = useState(false);

  const LABEL_FIELDS = [
    { key: "order_no", label: "Order No" },
    { key: "bundle_no", label: "Bundle/Pole #" },
    { key: "colour", label: "Butt Colour" },
    { key: "vol_per_pole", label: "Volume m³/Pole" },
    { key: "pieces", label: "Pieces" },
    { key: "total_cbm", label: "Total Bundle m³" },
    { key: "thick", label: "Thick (mm)" },
    { key: "wide", label: "Wide (mm)" },
    { key: "length", label: "Length" },
    { key: "special", label: "Special Instruction" },
  ];
  const isAdmin = user?.role === "admin";

  const shownOrders = statuses.length
    ? orders.filter((o) => (statusMode === "hide" ? !statuses.includes(o.status) : statuses.includes(o.status)))
    : orders;

  const filteredMatching = matching.filter((s) => {
    const f = matchFilter;
    if (f.butt && String(s.butt_size ?? "") !== String(f.butt)) return false;
    if (f.length && String(s.length ?? "") !== String(f.length)) return false;
    if (f.colour && (s.colour || "") !== f.colour) return false;
    if (f.q) {
      const hay = `${s.id} ${s.bundle_number || ""} ${s.origin || ""} ${s.delivery_note_number || ""}`.toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    return true;
  });
  const matchButtOpts = [...new Set(matching.map((s) => s.butt_size).filter((v) => v != null))].sort((a, b) => a - b);
  const matchLenOpts = [...new Set(matching.map((s) => s.length).filter((v) => v != null))].sort((a, b) => a - b);
  const matchColourOpts = [...new Set(matching.map((s) => s.colour).filter(Boolean))].sort();

  // Over-allocation guard: cannot select more poles than the order still needs.
  const selectedQty = matching.filter((s) => selectedStockIds.includes(s.id))
    .reduce((a, s) => a + (Number(s.quantity) || 1), 0);
  const outstandingQty = selectedOrder
    ? Math.max((selectedOrder.required_quantity || 0) - (selectedOrder.allocated_quantity || 0) - (selectedOrder.dispatched_quantity || 0), 0)
    : 0;
  const overAlloc = !!selectedOrder && (selectedOrder.required_quantity || 0) > 0 && selectedQty > outstandingQty;

  // Auto-pick exactly the outstanding quantity per order line (oldest rows first).
  const autoSelectRequired = () => {
    const lines = selectedOrder?.line_items || [];
    const remaining = lines.map((l) =>
      Math.max((Number(l.required_quantity) || 0) - (Number(l.allocated_quantity) || 0) - (Number(l.dispatched_quantity) || 0), 0));
    const matchesLine = (l, s) =>
      ["butt_size", "length", "colour", "product_id"].every((f) => l[f] == null || l[f] === "" || l[f] === s[f]);
    const picked = [];
    let pickedQty = 0;
    for (const s of filteredMatching) {
      const q = Number(s.quantity) || 1;
      const li = lines.findIndex((l, i) => remaining[i] >= q && matchesLine(l, s));
      if (li === -1) continue;
      remaining[li] -= q;
      picked.push(s.id);
      pickedQty += q;
      if (remaining.every((r) => r <= 0)) break;
    }
    setSelectedStockIds(picked);
    const short = remaining.reduce((a, r) => a + Math.max(r, 0), 0);
    if (!picked.length) {
      toast.info("Nothing to select — this order has no outstanding quantity (or no matching stock is shown).");
    } else if (short > 0) {
      toast.warning(`Auto-selected ${picked.length} row(s) = ${pickedQty} pole(s); still ${short} short — not enough matching stock available.`);
    } else {
      toast.success(`Auto-selected ${picked.length} row(s) = ${pickedQty} pole(s) — exactly what the order still needs.`);
    }
  };

  // Add a specific quantity from the rows currently SHOWN (filters applied),
  // capped so the order can never be over-allocated.
  const selectQtyFromShown = () => {
    const want = Math.floor(Number(autoQty)) || 0;
    if (want <= 0) {
      toast.error("Enter how many poles to select first");
      return;
    }
    const hasReq = (selectedOrder?.required_quantity || 0) > 0;
    const capacity = hasReq ? Math.max(outstandingQty - selectedQty, 0) : Infinity;
    if (capacity <= 0) {
      toast.warning("The order's full outstanding quantity is already selected or allocated");
      return;
    }
    const capped = Math.min(want, capacity);
    const sel = new Set(selectedStockIds);
    const added = [];
    let got = 0;
    for (const s of filteredMatching) {
      if (sel.has(s.id)) continue;
      const q = Number(s.quantity) || 1;
      if (q > capped - got) continue;
      added.push(s.id);
      got += q;
      if (got >= capped) break;
    }
    if (!added.length) {
      toast.info("No unselected rows shown that fit the requested quantity — adjust the filters");
      return;
    }
    setSelectedStockIds([...selectedStockIds, ...added]);
    if (capped < want) {
      toast.warning(`Selected ${got} pole(s) — capped from ${want} so the order isn't over-allocated`);
    } else if (got < want) {
      toast.warning(`Selected ${got} of ${want} pole(s) — that's all the shown stock has`);
    } else {
      toast.success(`Added ${added.length} row(s) = ${got} pole(s) from the shown stock`);
    }
  };

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    const r = await api.get("/orders", { params });
    setOrders(r.data);
  };

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/export-destinations").then((r) => setDestinations(r.data)).catch(() => {});
    api.get("/paint-colours").then((r) => {
      if (Array.isArray(r.data) && r.data.length > 0) {
        setColourMap(r.data.map((c) => ({ butt: c.butt_mm, colour: c.colour, swatch: c.swatch || "#888888" })));
      }
    }).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
  }, [q]); // eslint-disable-line

  const onCreate = async () => {
    try {
      const lines = (form.line_items || [])
        .filter((l) => l.butt_size !== "" || l.length !== "" || l.colour || l.required_quantity !== "")
        .map((l) => ({
          product_id: l.product_id || null, product_name: l.product_name || null,
          article_number: l.article_number || null,
          butt_size: numOrNull(l.butt_size), length: numOrNull(l.length), colour: l.colour || null,
          required_quantity: Number(l.required_quantity || 0),
          requires_pointing: !!l.requires_pointing, bundled: !!l.bundled,
          poles_per_bundle: numOrNull(l.poles_per_bundle),
        }));
      if (!lines.length) { toast.error("Add at least one order line"); return; }
      const p = {
        order_number: form.order_number,
        customer_id: form.customer_id || null, customer_name: form.customer_name || null,
        export_destination: form.export_destination || null,
        special_instructions: form.special_instructions || null,
        order_date: form.order_date || null,
        line_items: lines,
      };
      if (editingId) {
        await api.patch(`/orders/${editingId}`, p);
        toast.success("Order updated");
      } else {
        await api.post("/orders", p);
        toast.success("Order created");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openNew = () => {
    setForm({ ...empty, line_items: [blankLine()] });
    setEditingId(null);
    setOpen(true);
  };

  const lineFromLegacy = (o) => ({
    product_id: o.product_id || "", product_name: o.product_name || "", article_number: o.article_number || "",
    butt_size: o.butt_size ?? "", length: o.length ?? "", colour: o.colour || "",
    required_quantity: o.required_quantity ?? "",
    requires_pointing: !!o.requires_pointing, bundled: !!o.bundled, poles_per_bundle: o.poles_per_bundle ?? "",
  });

  const openEdit = (o) => {
    const lines = (o.line_items && o.line_items.length)
      ? o.line_items.map((l) => ({
          product_id: l.product_id || "", product_name: l.product_name || "", article_number: l.article_number || "",
          butt_size: l.butt_size ?? "", length: l.length ?? "", colour: l.colour || "",
          required_quantity: l.required_quantity ?? "",
          requires_pointing: !!l.requires_pointing, bundled: !!l.bundled, poles_per_bundle: l.poles_per_bundle ?? "",
        }))
      : [lineFromLegacy(o)];
    setForm({
      order_number: o.order_number || "",
      customer_id: o.customer_id || "", customer_name: o.customer_name || "",
      export_destination: o.export_destination || "",
      special_instructions: o.special_instructions || "",
      order_date: o.order_date || new Date().toISOString().slice(0, 10),
      line_items: lines,
    });
    setEditingId(o.id);
    setOpen(true);
  };

  // --- order line editor helpers ---
  const setLine = (i, patch) => setForm((f) => {
    const items = [...f.line_items];
    items[i] = { ...items[i], ...patch };
    return { ...f, line_items: items };
  });
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, blankLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const linesTotal = (form.line_items || []).reduce((a, l) => a + (Number(l.required_quantity) || 0), 0);

  const doDelete = async () => {
    try {
      await api.delete(`/orders/${delOrder.id}`, { params: { force: true } });
      toast.success("Order deleted");
      setDelOrder(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openOrderDetail = async (oid) => {
    const r = await api.get(`/orders/${oid}`);
    setSelectedOrder(r.data);
    const m = await api.get(`/orders/${oid}/matching-stock`);
    setMatching(m.data);
    setSelectedStockIds([]);
    setMatchFilter({ butt: "", length: "", colour: "", q: "" });
  };

  const allocate = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/allocate`, {
        order_id: selectedOrder.id,
        stock_item_ids: selectedStockIds,
      });
      toast.success(`Allocated ${selectedStockIds.length} bundles`);
      openOrderDetail(selectedOrder.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const release = async (sids) => {
    try {
      await api.post(`/orders/${selectedOrder.id}/release`, {
        order_id: selectedOrder.id,
        stock_item_ids: sids,
      });
      toast.success("Released");
      openOrderDetail(selectedOrder.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const releaseAll = async () => {
    const ids = (selectedOrder.allocated_stock || [])
      .filter((s) => s.status === "allocated").map((s) => s.id);
    if (!ids.length) { toast.error("No allocated stock to release"); return; }
    if (!confirm(`Release all ${ids.length} allocated bundle(s) back to available stock?`)) return;
    try {
      await api.post(`/orders/${selectedOrder.id}/release`, {
        order_id: selectedOrder.id,
        stock_item_ids: ids,
      });
      toast.success(`Released ${ids.length} bundle(s)`);
      openOrderDetail(selectedOrder.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const submitBundle = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/bundle`, {
        poles_per_bundle: bundleForm.poles_per_bundle === "" ? null : Number(bundleForm.poles_per_bundle),
        bundles_made: Number(bundleForm.bundles_made || 0),
        poles_bundled: Number(bundleForm.poles_bundled || 0),
      });
      toast.success("Bundling recorded");
      setBundleForm(null);
      openOrderDetail(selectedOrder.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const togglePrepared = async () => {
    try {
      const r = await api.post(`/orders/${selectedOrder.id}/prepare`, { prepared: !selectedOrder.prepared });
      setSelectedOrder({ ...selectedOrder, prepared: r.data.prepared });
      toast.success(r.data.prepared ? "Order marked as prepared / picked" : "Prepared flag removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update order");
    }
  };

  const labelStock = (selectedOrder?.allocated_stock || []).filter((s) => ["allocated", "dispatched"].includes(s.status));
  // One row per BUNDLE (bundled poles share a bundle number) or per loose stock row.
  const labelRows = (() => {
    const out = [], idx = {};
    for (const s of labelStock) {
      const bn = s.bundled && s.bundle_number ? s.bundle_number : null;
      if (bn && idx[bn]) { idx[bn].qty += Number(s.quantity) || 1; idx[bn].ids.push(s.id); continue; }
      const row = { ...s, qty: Number(s.quantity) || 1, ids: [s.id] };
      if (bn) idx[bn] = row;
      out.push(row);
    }
    return out;
  })();
  const labelCount = (r) => (r.bundled ? 2 : r.qty);
  const rowSelected = (r) => r.ids.every((id) => labelIds.includes(id));
  const totalLabels = labelRows.filter(rowSelected).reduce((a, r) => a + labelCount(r), 0);

  // Allocated Stock table: collapse bundled poles into one line per bundle.
  const allocRows = (() => {
    const out = [], idx = {};
    for (const s of (selectedOrder?.allocated_stock || [])) {
      const key = s.bundled && s.bundle_number ? `${s.bundle_number}|${s.status}` : null;
      if (key && idx[key]) {
        const g = idx[key];
        g.quantity = (Number(g.quantity) || 0) + (Number(s.quantity) || 1);
        g.cbm = (Number(g.cbm) || 0) + (Number(s.cbm) || 0);
        g.ids.push(s.id);
        continue;
      }
      const row = { ...s, ids: [s.id] };
      if (key) idx[key] = row;
      out.push(row);
    }
    return out;
  })();

  const openLabelModal = () => {
    setLabelIds(labelStock.map((s) => s.id));
    setLabelSpecial(selectedOrder?.special_instructions || "");
    setLabelFields(LABEL_FIELDS.map((f) => f.key));
    setLabelPaste("");
    setLabelPasteResult(null);
    setLabelModal(true);
  };

  const applyLabelPaste = () => {
    const tokens = labelPaste.split(/[\s,;]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
    if (!tokens.length) return;
    const set = new Set(tokens);
    const ids = [];
    const found = new Set();
    labelRows.forEach((r) => {
      const bn = (r.bundle_number || r.id.slice(0, 8)).toUpperCase();
      if (set.has(bn)) {
        ids.push(...r.ids);
        found.add(bn);
      }
    });
    setLabelIds(ids);
    setLabelPasteResult({ total: set.size, found: found.size, missing: [...set].filter((t) => !found.has(t)) });
  };

  const printLabels = async () => {
    setPrintingLabels(true);
    try {
      const excluded = LABEL_FIELDS.map((f) => f.key).filter((k) => !labelFields.includes(k));
      const r = await api.post(`/orders/${selectedOrder.id}/labels.pdf`,
        { stock_ids: labelIds, special_instruction: labelSpecial, excluded_fields: excluded },
        { responseType: "blob" });
      saveBlob(r, `labels-${selectedOrder.order_number}.pdf`);
      toast.success(`${totalLabels} labels generated`);
    } catch (e) {
      toast.error("Failed to generate labels");
    }
    setPrintingLabels(false);
  };

  const cancelOrder = async () => {
    if (!confirm("Cancel this order and release allocated stock?")) return;
    try {
      await api.post(`/orders/${selectedOrder.id}/cancel`);
      toast.success("Order cancelled");
      setSelectedOrder(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Orders"
        subtitle="Create export orders, allocate available stock and track to dispatch."
        actions={
          <>
            <Btn variant="secondary" as="a" href={`${API}/exports/orders.xlsx`} target="_blank" testid="export-orders">
              <Download className="h-4 w-4" /> Export
            </Btn>
            {can(user?.role, "orders:create") && (
              <Btn onClick={openNew} testid="new-order-btn">
                <Plus className="h-4 w-4" /> New Order
              </Btn>
            )}
          </>
        }
      />

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search order #, customer, container..." testid="orders-search" />
        <div className="relative">
          <button
            onClick={() => setStatusOpen((v) => !v)}
            className="h-9 px-3 rounded-sm border border-stone-300 bg-white text-sm flex items-center gap-1.5 hover:border-stone-400"
            data-testid="filter-order-status"
          >
            <Filter className="h-3.5 w-3.5 text-stone-500" />
            {statuses.length === 0
              ? "All statuses"
              : `${statusMode === "hide" ? "Hiding" : "Showing"} ${statuses.length} status${statuses.length > 1 ? "es" : ""}`}
            <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
          </button>
          {statusOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} />
              <div className="absolute z-20 mt-1 w-64 bg-white border border-stone-200 rounded-sm shadow-lg p-2"
                   data-testid="status-filter-panel">
                <div className="flex gap-3 text-xs pb-2 mb-1 border-b border-stone-100">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={statusMode === "show"} onChange={() => setStatusMode("show")}
                           data-testid="status-mode-show" />
                    Show selected
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={statusMode === "hide"} onChange={() => setStatusMode("hide")}
                           data-testid="status-mode-hide" />
                    Exclude selected
                  </label>
                </div>
                {STATUS_OPTS.map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 hover:bg-stone-50 rounded-sm">
                    <input
                      type="checkbox"
                      checked={statuses.includes(v)}
                      onChange={() => setStatuses((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]))}
                      data-testid={`status-opt-${v}`}
                    />
                    <StatusBadge status={v} />
                  </label>
                ))}
                <div className="pt-1 mt-1 border-t border-stone-100">
                  <button onClick={() => setStatuses([])}
                          className="text-xs text-stone-500 hover:text-stone-800 hover:underline"
                          data-testid="status-filter-clear">
                    Clear — show all
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="text-xs text-stone-500 ml-auto" data-testid="orders-count">
          {shownOrders.length === orders.length ? `${orders.length} orders` : `${shownOrders.length} of ${orders.length} orders`}
        </div>
      </Toolbar>

      <DataTable
        testid="orders-table"
        onRowClick={(r) => openOrderDetail(r.id)}
        columns={[
          { header: "Order #", mono: true, render: (r) => <span className="font-semibold">{r.order_number}</span> },
          { header: "Customer", render: (r) => (
            <div>
              <div>{r.customer_name || "—"}</div>
              <div className="text-xs text-stone-500">{r.export_destination}</div>
            </div>
          )},
          { header: "Lines / Spec", render: (r) => {
            const lines = (r.line_items && r.line_items.length) ? r.line_items : null;
            if (lines) {
              return (
                <div className="text-xs">
                  <div>{lines.length} line{lines.length > 1 ? "s" : ""}</div>
                  <div className="text-stone-500 font-mono">
                    {lines.slice(0, 2).map((l) => `${l.butt_size ?? "—"}mm·${l.length ?? "—"}m·${l.colour || "—"}`).join("; ")}
                    {lines.length > 2 ? ` +${lines.length - 2}` : ""}
                  </div>
                </div>
              );
            }
            return (
              <div className="text-xs text-stone-500 font-mono">{r.butt_size != null ? `${r.butt_size}mm` : "—"} · {r.length ? `${r.length}m` : "—"} · {r.colour || "—"}</div>
            );
          }},
          { header: "Required", mono: true, align: "right", render: (r) => fmtNumber(r.required_quantity) },
          { header: "Allocated", mono: true, align: "right", render: (r) => (
            <span className={r.allocated_quantity >= r.required_quantity ? "text-emerald-700 font-semibold" : ""}>
              {fmtNumber(r.allocated_quantity || 0)}
            </span>
          )},
          { header: "Dispatched", mono: true, align: "right", render: (r) => (
            <span className={(r.dispatched_quantity || 0) > 0 ? "text-amber-700 font-semibold" : "text-stone-400"} data-testid={`order-dispatched-${r.id}`}>
              {fmtNumber(r.dispatched_quantity || 0)}
            </span>
          )},
          { header: "Status", render: (r) => (
            <div className="flex items-center gap-1.5">
              <StatusBadge status={r.status} />
              {r.prepared && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid={`order-prepared-${r.id}`}>
                  <PackageCheck className="h-3 w-3" /> Picked
                </span>
              )}
            </div>
          )},
          { header: "Actions", align: "right", render: (r) => (
            <div className="flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => window.open(`${API}/orders/${r.id}/order.pdf`, "_blank")}
                      className="text-stone-500 hover:text-emerald-800" title="Print Order (PDF)"
                      data-testid={`order-print-${r.id}`}>
                <Printer className="h-4 w-4" />
              </button>
              {can(user?.role, "orders:update") && (
                <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`order-edit-${r.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setDelOrder(r)} className="text-stone-500 hover:text-red-700" title="Delete" data-testid={`order-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <ArrowRight className="h-4 w-4 text-stone-400 cursor-pointer" onClick={() => openOrderDetail(r.id)} />
            </div>
          )},
        ]}
        rows={shownOrders}
      />

      {/* New / Edit Order */}
      <Modal
        open={open} onClose={() => { setOpen(false); setEditingId(null); }} title={editingId ? "Edit Order" : "New Order"} size="lg"
        footer={<><Btn variant="secondary" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Btn><Btn onClick={onCreate} testid="order-save">{editingId ? "Save Changes" : "Create"}</Btn></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Order Number" required>
            <Input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} data-testid="order-number" />
            <OrderDupWarning value={form.order_number} excludeId={editingId} />
          </Field>
          <Field label="Order Date">
            <Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} />
          </Field>
          <Field label="Customer">
            <Select value={form.customer_id} onChange={(e) => {
              const c = customers.find((x) => x.id === e.target.value);
              setForm({ ...form, customer_id: e.target.value, customer_name: c?.name || "" });
            }} data-testid="order-customer">
              <option value="">Select...</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Export Destination">
            <Select value={form.export_destination} onChange={(e) => setForm({ ...form, export_destination: e.target.value })} data-testid="order-export-destination">
              <option value="">Select...</option>
              {form.export_destination && !destinations.some((d) => d.code === form.export_destination) && (
                <option value={form.export_destination}>{form.export_destination}</option>
              )}
              {destinations.filter((d) => d.active !== false).map((d) => <option key={d.id} value={d.code}>{d.code}</option>)}
            </Select>
          </Field>
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="data-label">Order Lines — sizes &amp; diameters {linesTotal > 0 && <span className="text-stone-500">(total {fmtNumber(linesTotal)} pcs)</span>}</div>
              <Btn variant="secondary" onClick={addLine} testid="order-add-line"><Plus className="h-3.5 w-3.5" /> Add Line</Btn>
            </div>
            <div className="overflow-x-auto border border-stone-200 rounded-md" data-testid="order-lines-editor">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Diameter (mm)</th>
                    <th className="px-2 py-1.5 text-left">Size / Length (m)</th>
                    <th className="px-2 py-1.5 text-left">Colour</th>
                    <th className="px-2 py-1.5 text-left">Article #</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-center">Point?</th>
                    <th className="px-2 py-1.5 text-center">Bundle?</th>
                    <th className="px-2 py-1.5 text-right">P/Bundle</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.line_items || []).map((l, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`order-line-${i}`}>
                      <td className="px-2 py-1"><Input type="number" step="0.1" value={l.butt_size} onChange={(e) => {
                        const v = e.target.value;
                        const auto = v ? colourForButt(v, colourMap) : null;
                        setLine(i, { butt_size: v, ...(auto ? { colour: auto.colour } : {}) });
                      }} placeholder="110" data-testid={`order-line-butt-${i}`} /></td>
                      <td className="px-2 py-1"><LengthInput value={l.length} onChange={(v) => setLine(i, { length: v })} presets={LENGTHS} listId="order-len-presets" /></td>
                      <td className="px-2 py-1">
                        <Select value={l.colour} onChange={(e) => setLine(i, { colour: e.target.value })} data-testid={`order-line-colour-${i}`}>
                          <option value="">Any</option>
                          {COLOURS.map((c) => <option key={c}>{c}</option>)}
                        </Select>
                      </td>
                      <td className="px-2 py-1"><Input value={l.article_number} onChange={(e) => setLine(i, { article_number: e.target.value })} /></td>
                      <td className="px-2 py-1"><Input type="number" value={l.required_quantity} onChange={(e) => setLine(i, { required_quantity: e.target.value })} className="text-right" data-testid={`order-line-qty-${i}`} /></td>
                      <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!l.requires_pointing} onChange={(e) => setLine(i, { requires_pointing: e.target.checked })} data-testid={`order-line-point-${i}`} /></td>
                      <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!l.bundled} onChange={(e) => setLine(i, { bundled: e.target.checked })} data-testid={`order-line-bundle-${i}`} /></td>
                      <td className="px-2 py-1"><Input type="number" value={l.poles_per_bundle} onChange={(e) => setLine(i, { poles_per_bundle: e.target.value })} disabled={!l.bundled} placeholder={l.bundled ? "20" : ""} className="text-right" data-testid={`order-line-ppb-${i}`} /></td>
                      <td className="px-2 py-1 text-center">
                        {(form.line_items || []).length > 1 && (
                          <button onClick={() => removeLine(i)} className="text-stone-400 hover:text-red-600" title="Remove line" data-testid={`order-line-remove-${i}`}><X className="h-4 w-4" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:col-span-3">
            <ContainerEstimate lines={form.line_items} testid="order-form-container-estimate" />
          </div>
          <div className="md:col-span-3">
            <Field label="Special Instructions">
              <Textarea rows={2} value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} />
            </Field>
          </div>
        </div>
      </Modal>

      {/* Order Detail / Allocation */}
      {selectedOrder && (
        <Modal
          open={!!selectedOrder} onClose={() => setSelectedOrder(null)}
          title={`Order ${selectedOrder.order_number}`} size="xl"
          testid="order-detail-modal"
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {can(user?.role, "orders:update") && selectedOrder.status !== "cancelled" && (
                <Btn variant={selectedOrder.prepared ? "secondary" : "primary"} onClick={togglePrepared} testid="order-prepared-toggle">
                  <PackageCheck className="h-4 w-4" />
                  {selectedOrder.prepared ? "Prepared / Picked ✓ (undo)" : "Mark as Prepared / Picked"}
                </Btn>
              )}
              {selectedOrder.prepared && labelStock.length > 0 && (
                <Btn onClick={openLabelModal} testid="order-print-labels-btn">
                  <Tag className="h-4 w-4" /> Print Labels
                </Btn>
              )}
              <Btn variant="secondary" as="a" href={`${API}/orders/${selectedOrder.id}/order.pdf`}
                   target="_blank" rel="noreferrer" testid="order-print-pdf">
                <Download className="h-4 w-4" /> Print Order (PDF)
              </Btn>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="data-label">Customer</div><div>{selectedOrder.customer_name}</div></div>
              <div><div className="data-label">Destination</div><div>{selectedOrder.export_destination}</div></div>
              <div><div className="data-label">Status</div><StatusBadge status={selectedOrder.status} /></div>
              <div><div className="data-label">Required</div><div className="font-mono">{fmtNumber(selectedOrder.required_quantity)} pcs</div></div>
              <div><div className="data-label">Allocated</div><div className="font-mono">{fmtNumber(selectedOrder.allocated_quantity)} pcs · {fmtNumber(selectedOrder.allocated_cbm, 3)} CBM</div></div>
              <div><div className="data-label">Dispatched</div><div className="font-mono" data-testid="order-detail-dispatched">{fmtNumber(selectedOrder.dispatched_quantity || 0)} pcs</div></div>
              <div><div className="data-label">Allocated Weight</div><div className="font-mono">{fmtNumber(selectedOrder.allocated_weight)} kg</div></div>
            </div>

            <div>
              <div className="data-label mb-1.5">Order Lines</div>
              <div className="overflow-x-auto border border-stone-200 rounded-md" data-testid="order-detail-lines">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left">#</th>
                      <th className="px-2 py-1.5 text-left">Diameter</th>
                      <th className="px-2 py-1.5 text-left">Size</th>
                      <th className="px-2 py-1.5 text-left">Colour</th>
                      <th className="px-2 py-1.5 text-center">Point / Bundle</th>
                      <th className="px-2 py-1.5 text-right">Alloc / Disp / Req</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedOrder.line_items || []).map((l, i) => {
                      const done = ((l.allocated_quantity || 0) + (l.dispatched_quantity || 0)) >= (l.required_quantity || 0);
                      return (
                        <tr key={i} className="border-t border-stone-100" data-testid={`order-detail-line-${i}`}>
                          <td className="px-2 py-1.5 text-stone-500">{i + 1}</td>
                          <td className="px-2 py-1.5 font-mono">{l.butt_size != null ? `${l.butt_size}mm` : "—"}</td>
                          <td className="px-2 py-1.5 font-mono">{l.length != null ? `${l.length}m` : "—"}</td>
                          <td className="px-2 py-1.5">{l.colour || "—"}</td>
                          <td className="px-2 py-1.5 text-center text-xs">
                            {l.requires_pointing && <span className="text-emerald-700 mr-1">Point</span>}
                            {l.bundled && <span className="text-sky-700">Bundle{l.poles_per_bundle ? ` @${l.poles_per_bundle}` : ""}</span>}
                            {!l.requires_pointing && !l.bundled && <span className="text-stone-400">—</span>}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${done ? "text-emerald-700 font-semibold" : ""}`}>
                            {fmtNumber(l.allocated_quantity || 0)} / <span className="text-amber-700">{fmtNumber(l.dispatched_quantity || 0)}</span> / {fmtNumber(l.required_quantity || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <ContainerEstimate key={selectedOrder.id} storageKey={`containerEstimateRules:${selectedOrder.id}`}
                               lines={(selectedOrder.line_items || []).map((l) => withStockBundles(l, stockBundleMap(selectedOrder.allocated_stock)))}
                               testid="order-detail-container-estimate" />

            {selectedOrder.status !== "cancelled" && selectedOrder.status !== "dispatched" && (
              <div className="flex justify-end gap-2">
                <Btn variant="danger" onClick={cancelOrder} testid="order-cancel-btn"><Ban className="h-4 w-4" /> Cancel Order</Btn>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-base font-semibold">Allocated Stock ({allocRows.length})</div>
                {can(user?.role, "orders:allocate")
                  && (selectedOrder.allocated_stock || []).some((s) => s.status === "allocated")
                  && selectedOrder.status !== "cancelled" && (
                  <Btn variant="secondary" onClick={releaseAll} testid="deallocate-all-btn">
                    <X className="h-4 w-4" /> Deallocate All
                  </Btn>
                )}
              </div>
              <DataTable
                columns={[
                  { header: "Bundle / Pole #", mono: true, render: (r) => (
                    <span className="text-xs">
                      {r.bundle_number ? <span className="font-semibold">{r.bundle_number}</span> : <span>{r.id.slice(0, 8)}…</span>}
                      {r.bundled && <span className="ml-1.5 text-[10px] font-bold uppercase text-sky-700 bg-sky-50 px-1 py-0.5 rounded-sm">Bundle</span>}
                    </span>
                  )},
                  { header: "Spec", render: (r) => <span className="font-mono text-xs">{r.butt_size != null ? `${r.butt_size}mm` : "—"} · {r.length ? `${r.length}m` : "—"} · {r.colour || "—"}</span> },
                  { header: "Origin", key: "origin" },
                  { header: "Qty", align: "right", mono: true, render: (r) => fmtNumber(r.quantity) },
                  { header: "CBM", align: "right", mono: true, render: (r) => fmtNumber(r.cbm, 3) },
                  { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
                  { header: "", render: (r) => r.status === "allocated" && (
                    <button onClick={(e) => { e.stopPropagation(); release(r.ids); }} className="text-red-700 text-xs hover:underline" data-testid={`release-${r.id}`}>
                      <X className="h-3 w-3 inline" /> Release
                    </button>
                  )},
                ]}
                rows={allocRows}
                empty="No stock allocated yet."
              />
            </div>

            {can(user?.role, "orders:allocate") && selectedOrder.status !== "cancelled" && selectedOrder.status !== "dispatched" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-base font-semibold">Available Matching Stock ({filteredMatching.length}{filteredMatching.length !== matching.length ? ` of ${matching.length}` : ""})</div>
                  <Btn onClick={allocate} disabled={selectedStockIds.length === 0 || overAlloc} testid="allocate-btn">
                    Allocate {selectedStockIds.length} bundle(s)
                  </Btn>
                </div>
                {overAlloc && (
                  <div className="text-xs text-red-600 font-semibold mb-2 flex items-center gap-1" data-testid="over-alloc-warning">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Over-allocation blocked: only {fmtNumber(outstandingQty)} pole(s) still outstanding on this order — you selected {fmtNumber(selectedQty)}. Deselect {fmtNumber(selectedQty - outstandingQty)} pole(s).
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="matching-filters">
                  <Select value={matchFilter.butt} onChange={(e) => setMatchFilter({ ...matchFilter, butt: e.target.value })} data-testid="match-filter-butt">
                    <option value="">All diameters</option>
                    {matchButtOpts.map((b) => <option key={b} value={b}>{b}mm</option>)}
                  </Select>
                  <Select value={matchFilter.length} onChange={(e) => setMatchFilter({ ...matchFilter, length: e.target.value })} data-testid="match-filter-length">
                    <option value="">All lengths</option>
                    {matchLenOpts.map((l) => <option key={l} value={l}>{l}m</option>)}
                  </Select>
                  <Select value={matchFilter.colour} onChange={(e) => setMatchFilter({ ...matchFilter, colour: e.target.value })} data-testid="match-filter-colour">
                    <option value="">All colours</option>
                    {matchColourOpts.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                  <Input value={matchFilter.q} onChange={(e) => setMatchFilter({ ...matchFilter, q: e.target.value })} placeholder="Search ID / bundle / origin / DN" className="flex-1 min-w-[180px]" data-testid="match-filter-search" />
                  <button onClick={autoSelectRequired} className="text-xs font-semibold text-emerald-800 hover:underline" title="Selects exactly the outstanding quantity for each order line from the rows shown (oldest first)" data-testid="match-auto-select">Auto-select required ({fmtNumber(outstandingQty)})</button>
                  <span className="flex items-center gap-1 pl-1 border-l border-stone-200">
                    <Input type="number" min="1" value={autoQty} onChange={(e) => setAutoQty(e.target.value)}
                           placeholder="Qty" className="w-20 h-8 text-xs" data-testid="match-auto-qty" />
                    <button onClick={selectQtyFromShown} className="text-xs font-semibold text-emerald-800 hover:underline"
                            title="Adds this many poles from the rows shown (top-down, respecting the filters) without over-allocating the order"
                            data-testid="match-select-qty">
                      + Select qty from shown
                    </button>
                  </span>
                  <button onClick={() => setShowCutPlan(true)} className="text-xs font-semibold text-teal-800 hover:underline inline-flex items-center gap-1" title="Suggest which longer poles to cut to cover the shortage with minimum waste — also cancel applied cut plans" data-testid="open-cut-plan">
                    <Scissors className="h-3 w-3" /> Cut Plan
                  </button>
                  <button onClick={() => setSelectedStockIds([...new Set([...selectedStockIds, ...filteredMatching.map((s) => s.id)])])} className="text-xs text-emerald-800 hover:underline" data-testid="match-select-filtered">Select all shown</button>
                  {selectedStockIds.length > 0 && (
                    <button onClick={() => setSelectedStockIds([])} className="text-xs text-stone-500 hover:underline" data-testid="match-clear-selection">Clear selection</button>
                  )}
                  {(matchFilter.butt || matchFilter.length || matchFilter.colour || matchFilter.q) && (
                    <button onClick={() => setMatchFilter({ butt: "", length: "", colour: "", q: "" })} className="text-xs text-stone-500 hover:underline">Clear</button>
                  )}
                </div>
                <div className="panel max-h-80 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-stone-100 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"></th>
                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Bundle / Pole #</th>
                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Spec</th>
                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Origin</th>
                        <th className="px-3 py-2 text-right text-xs uppercase tracking-wider">Qty</th>
                        <th className="px-3 py-2 text-right text-xs uppercase tracking-wider">CBM</th>
                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatching.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-stone-400 text-sm">No matching available stock.</td></tr>
                      )}
                      {filteredMatching.map((s) => {
                        const sel = selectedStockIds.includes(s.id);
                        return (
                          <tr key={s.id} className={`border-b hover:bg-stone-50 ${sel ? "bg-emerald-50" : ""}`}>
                            <td className="px-3 py-1.5">
                              <input type="checkbox" checked={sel} onChange={(e) => setSelectedStockIds(
                                e.target.checked ? [...selectedStockIds, s.id] : selectedStockIds.filter((x) => x !== s.id)
                              )} data-testid={`select-stock-${s.id}`} />
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs" data-testid={`match-bundle-${s.id}`}>
                              {s.bundle_number
                                ? <span className="font-semibold text-stone-800">{s.bundle_number}</span>
                                : <span className="text-stone-400">{s.id.slice(0, 8)}…</span>}
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs">{s.butt_size != null ? `${s.butt_size}mm` : "—"} · {s.length ? `${s.length}m` : "—"} · {s.colour || "—"}</td>
                            <td className="px-3 py-1.5">{s.origin}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(s.quantity)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(s.cbm, 3)}</td>
                            <td className="px-3 py-1.5 font-mono text-xs">{s.date_received}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
      <Modal
        open={!!bundleForm}
        onClose={() => setBundleForm(null)}
        title="Mark Order Bundled"
        size="sm"
        testid="bundle-modal"
        footer={<><Btn variant="secondary" onClick={() => setBundleForm(null)}>Cancel</Btn><Btn onClick={submitBundle} testid="bundle-save">Save Bundling</Btn></>}
      >
        {bundleForm && (
          <div className="space-y-3">
            <Field label="Poles per Bundle">
              <Input type="number" value={bundleForm.poles_per_bundle} onChange={(e) => setBundleForm({ ...bundleForm, poles_per_bundle: e.target.value })} placeholder="e.g. 20" data-testid="bundle-poles-per-bundle" />
            </Field>
            <Field label="Bundles Made">
              <Input type="number" value={bundleForm.bundles_made} onChange={(e) => setBundleForm({ ...bundleForm, bundles_made: e.target.value })} data-testid="bundle-bundles-made" />
            </Field>
            <Field label="Poles Bundled">
              <Input type="number" value={bundleForm.poles_bundled} onChange={(e) => setBundleForm({ ...bundleForm, poles_bundled: e.target.value })} data-testid="bundle-poles-bundled" />
            </Field>
          </div>
        )}
      </Modal>

      {/* Print pole / bundle labels */}
      {labelModal && selectedOrder && (
        <Modal
          open={labelModal} onClose={() => setLabelModal(false)}
          title={`Print Labels — ${selectedOrder.order_number}`} size="lg"
          testid="labels-modal"
        >
          <div className="space-y-3">
            <div className="text-sm text-stone-600">
              Labels are 10cm × 4.5cm, 12 per A4 page with cut lines. Each bundle gets 2 labels
              (front &amp; back); loose poles get one label per pole. Untick anything you don't
              want to reprint.
            </div>
            <div className="flex items-center gap-3 text-xs">
              <button onClick={() => setLabelIds(labelStock.map((s) => s.id))} className="text-emerald-800 hover:underline" data-testid="labels-select-all">Select all</button>
              <button onClick={() => setLabelIds([])} className="text-stone-500 hover:underline" data-testid="labels-select-none">Select none</button>
            </div>
            <div className="border border-stone-200 rounded-sm bg-stone-50 p-2.5 space-y-1.5">
              <div className="text-xs text-stone-600 font-semibold">
                Paste bundle numbers from Excel to select only those (one per line, or separated by spaces/commas):
              </div>
              <div className="flex gap-2 items-start">
                <textarea rows={2} value={labelPaste} onChange={(e) => setLabelPaste(e.target.value)}
                          placeholder={"BDL-0001  BDL-0002  BDL-0003"}
                          className="flex-1 px-2 py-1.5 border border-stone-300 rounded-sm text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-800/30"
                          data-testid="labels-bundle-paste-input" />
                <Btn size="sm" variant="secondary" onClick={applyLabelPaste} testid="labels-bundle-paste-apply">Select pasted</Btn>
              </div>
              {labelPasteResult && (
                <div className="text-xs" data-testid="labels-bundle-paste-result">
                  <span className="text-emerald-800 font-semibold">{labelPasteResult.found} of {labelPasteResult.total} pasted numbers matched and selected.</span>
                  {labelPasteResult.missing.length > 0 && (
                    <span className="text-amber-700"> Not on this order: <span className="font-mono">{labelPasteResult.missing.slice(0, 20).join(", ")}{labelPasteResult.missing.length > 20 ? "…" : ""}</span></span>
                  )}
                </div>
              )}
            </div>
            <div className="panel max-h-80 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-stone-100 border-b">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Bundle / Pole #</th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider">Spec</th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wider">Pieces</th>
                    <th className="px-3 py-2 text-center text-xs uppercase tracking-wider">Bundled</th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wider">Labels</th>
                  </tr>
                </thead>
                <tbody>
                  {labelRows.map((r) => {
                    const sel = rowSelected(r);
                    return (
                      <tr key={r.id} className={`border-b ${sel ? "bg-emerald-50" : ""}`}>
                        <td className="px-3 py-1.5">
                          <input type="checkbox" checked={sel} onChange={(e) => setLabelIds(
                            e.target.checked ? [...new Set([...labelIds, ...r.ids])] : labelIds.filter((x) => !r.ids.includes(x))
                          )} data-testid={`label-select-${r.id}`} />
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          {r.bundle_number || `${r.id.slice(0, 8).toUpperCase()}`}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.butt_size != null ? `${r.butt_size}mm` : "—"} · {r.length ? `${r.length}m` : "—"} · {r.colour || "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(r.qty)}</td>
                        <td className="px-3 py-1.5 text-center text-xs">{r.bundled ? <span className="text-sky-700 font-semibold">Yes</span> : <span className="text-stone-400">No</span>}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{labelCount(r)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Field label="Data to show on the label (unticked fields print blank)">
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 border border-stone-200 rounded-sm p-2.5 bg-stone-50" data-testid="labels-field-picker">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={labelFields.length === LABEL_FIELDS.length}
                    onChange={(e) => setLabelFields(e.target.checked ? LABEL_FIELDS.map((f) => f.key) : [])}
                    data-testid="labels-field-all"
                  />
                  All data
                </label>
                {LABEL_FIELDS.map((f) => (
                  <label key={f.key} className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={labelFields.includes(f.key)}
                      onChange={(e) => setLabelFields(
                        e.target.checked ? [...labelFields, f.key] : labelFields.filter((k) => k !== f.key)
                      )}
                      data-testid={`labels-field-${f.key}`}
                    />
                    {f.label}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Special Instruction (printed on every label)">
              <Textarea
                rows={2}
                value={labelSpecial}
                onChange={(e) => setLabelSpecial(e.target.value)}
                placeholder="e.g. Handle with care — keep dry"
                disabled={!labelFields.includes("special")}
                data-testid="labels-special-instruction"
              />
            </Field>
            <div className="flex items-center justify-between">
              <div className="text-sm text-stone-600" data-testid="labels-summary">
                <span className="font-semibold">{totalLabels}</span> labels · {Math.max(1, Math.ceil(totalLabels / 12))} A4 page(s)
              </div>
              <Btn onClick={printLabels} disabled={labelIds.length === 0 || printingLabels} testid="labels-download-btn">
                <Printer className="h-4 w-4" /> {printingLabels ? "Generating…" : "Download Labels PDF"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
      {showCutPlan && selectedOrder && (
        <CutPlanModal
          order={selectedOrder}
          onClose={() => setShowCutPlan(false)}
          onApplied={() => openOrderDetail(selectedOrder.id)}
        />
      )}
      <ConfirmDialog
        open={!!delOrder}
        onClose={() => setDelOrder(null)}
        onConfirm={doDelete}
        title="Delete Order"
        confirmLabel="Delete Order"
        testid="order-delete-confirm"
        message={delOrder ? `Delete order ${delOrder.order_number}? Any allocated stock will be released back to available. Dispatched orders cannot be deleted. This cannot be undone.` : ""}
      />
    </AppLayout>
  );
}
